import { Currency } from "@redotech/money/currencies";
import { composeWrappers } from "@redotech/react-util/component";
import { CurrencyConverterProvider } from "@redotech/react-util/money";
import { useBreadcrumbs } from "@redotech/redo-web/breadcrumb";
import { NotificationProvider } from "@redotech/redo-web/notification-provider";
import { CustomSnackbarProvider } from "@redotech/redo-web/snackbar";
import { ThemeProvider } from "@redotech/redo-web/theme-provider";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { memo, useContext } from "react";
import { createRoot } from "react-dom/client";
import {
  Navigate,
  Outlet,
  RouterProvider,
  createBrowserRouter,
} from "react-router-dom";
import { useDocumentTitle } from "usehooks-ts";
import { aeoDashboardRoutes } from "./aeo-dashboard/route";
import { agentRoutes } from "./agents/navigation/route";
import { RedoAdminAgentsRpcClientProvider } from "./agents/providers/redo-admin-agents-rpc-client-provider";
import { aiSettingsRequestRoutes } from "./ai-settings-requests/route";
import { conversionMonitorRoutes } from "./analytics/conversion-monitor/route";
import { AuthContext, AuthProvider } from "./app/auth";
import { RedoAdminMarketingRpcClientProvider } from "./app/redo-admin-marketing-rpc-client-provider";
import {
  RedoAdminRpcClientContext,
  RedoAdminRpcClientProvider,
} from "./app/redo-admin-rpc-client-provider";
import { TanStackQueryDevtools } from "./app/tanstack-query-devtools";
import { UserProvider } from "./app/user";
import { auditLogsRoutes } from "./audit-logs/route";
import { ccClientHealthRoutes } from "./cc-client-health/route";
import { RedoAdminClient } from "./client";
import { RedoAdminClientContext } from "./client/context";
import { REDO_ADMIN_SERVER_URL } from "./config";
import { globalSearchRoutes } from "./global-search/route";
import { goldenDomeRoutes } from "./golden-dome/route";
import { incidentsRoutes } from "./incidents/incidents-routes";
import { loginRoutes } from "./login/route";
import { marketingRoutes } from "./marketing/route";
import { rateCalculatorRoutes } from "./oms-pricing-tool/route";
import { onboardingsRoutes } from "./onboardings/route";
import { pmAgentRoutes } from "./pm-agent/route";
import { reclaimDisputesRoutes } from "./reclaim-disputes/route";
import { redoUsesRedoRoutes } from "./redo-uses-redo/route";
import { aiExchangeAnalyticsRoutes } from "./returns/ai-exchange-analytics/route";
import { fraudReviewRoutes } from "./returns/fraud-review/route";
import { integrationMetricsRoutes } from "./returns/integration-metrics/route";
import { marketRateCalculationRoutes } from "./returns/market-rate-calculation/route";
import { temporalRoutes } from "./returns/temporal/route";
import { settingsAgentRoutes } from "./settings-agent/route";
import { shippingClaimRoutes } from "./shipping-claims/route";
import { slackMessagingRoutes } from "./slack-messaging/route";
import "./styles.css";
import { supportRoutes } from "./support/route";
import { teamRoutes } from "./team/route";
import { ticketValidationRoutes } from "./ticket-validation/route";
import { warehouseManagementRoutes } from "./warehouse-management/navigation/route";

const RouterRoot = memo(function RouterRoot() {
  const breadcrumbs = useBreadcrumbs();
  useDocumentTitle(
    breadcrumbs
      ? `${breadcrumbs
          .map((breadcrumb) => breadcrumb.name)
          .join(" ")} — Redo Admin`
      : "Redo Admin",
  );
  return <Outlet />;
});

const router = createBrowserRouter([
  {
    path: "/",
    element: <RouterRoot />,
    // errorElement: <div>Not Found</div>,
    children: [
      { index: true, element: <Navigate to="login" /> },
      { path: "login", element: <Outlet />, children: loginRoutes },
      { path: "teams", element: <Outlet />, children: teamRoutes },
      { path: "agents", element: <Outlet />, children: agentRoutes },
      {
        path: "global-search",
        element: <Outlet />,
        children: globalSearchRoutes,
      },
      { path: "marketing", element: <Outlet />, children: marketingRoutes },
      { path: "onboardings", element: <Outlet />, children: onboardingsRoutes },
      { path: "incidents", element: <Outlet />, children: incidentsRoutes },
      { path: "audit-logs", element: <Outlet />, children: auditLogsRoutes },
      {
        path: "rate-calculator",
        element: <Outlet />,
        children: rateCalculatorRoutes,
      },
      {
        path: "ai-exchange-analytics",
        element: <Outlet />,
        children: aiExchangeAnalyticsRoutes,
      },
      {
        path: "redo-uses-redo",
        element: <Outlet />,
        children: redoUsesRedoRoutes,
      },
      {
        path: "slack-messaging",
        element: <Outlet />,
        children: slackMessagingRoutes,
      },
      { path: "golden-dome", element: <Outlet />, children: goldenDomeRoutes },
      { path: "support", element: <Outlet />, children: supportRoutes },
      {
        path: "carrier-claims",
        element: <Outlet />,
        children: shippingClaimRoutes,
      },
      { path: "temporal", element: <Outlet />, children: temporalRoutes },
      {
        path: "fraud-review",
        element: <Outlet />,
        children: fraudReviewRoutes,
      },
      {
        path: "integration-metrics",
        element: <Outlet />,
        children: integrationMetricsRoutes,
      },
      {
        path: "market-rate-calculation",
        element: <Outlet />,
        children: marketRateCalculationRoutes,
      },
      {
        path: "ai-settings-requests",
        element: <Outlet />,
        children: aiSettingsRequestRoutes,
      },
      {
        path: "settings-agent",
        element: <Outlet />,
        children: settingsAgentRoutes,
      },
      {
        path: "warehouse-management",
        element: <Outlet />,
        children: warehouseManagementRoutes,
      },
      {
        path: "cc-client-health",
        element: <Outlet />,
        children: ccClientHealthRoutes,
      },
      {
        path: "analytics/conversion-monitor",
        element: <Outlet />,
        children: conversionMonitorRoutes,
      },
      {
        path: "ticket-validation",
        element: <Outlet />,
        children: ticketValidationRoutes,
      },
      { path: "pm-agent", element: <Outlet />, children: pmAgentRoutes },
      { path: "aeo", element: <Outlet />, children: aeoDashboardRoutes },
      {
        path: "reclaim-disputes",
        element: <Outlet />,
        children: reclaimDisputesRoutes,
      },
    ],
  },
]);

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 2,
      staleTime: 5 * 60 * 1000, // 5 minutes
    },
  },
});

const AdminApp = () => {
  return composeWrappers(
    (inner) => (
      <QueryClientProvider client={queryClient}>
        {inner}
        <TanStackQueryDevtools />
      </QueryClientProvider>
    ),
    (inner) => <NotificationProvider>{inner}</NotificationProvider>,
    (inner) => <CustomSnackbarProvider>{inner}</CustomSnackbarProvider>,
    (inner) => <AuthProvider>{inner}</AuthProvider>,
    (inner) => <ThemeProvider>{inner}</ThemeProvider>,
    (inner) => (
      <AuthContext.Consumer>
        {(auth) => (
          <RedoAdminRpcClientProvider
            authToken={auth?.token}
            baseUrl={REDO_ADMIN_SERVER_URL}
          >
            <RedoAdminAgentsRpcClientProvider
              authToken={auth?.token}
              baseUrl={REDO_ADMIN_SERVER_URL}
            >
              <RedoAdminMarketingRpcClientProvider
                authToken={auth?.token}
                baseUrl={REDO_ADMIN_SERVER_URL}
              >
                <RedoAdminClientContext.Provider
                  value={
                    new RedoAdminClient(REDO_ADMIN_SERVER_URL, auth?.token)
                  }
                >
                  <MoneyWrapper>
                    <UserProvider>{inner}</UserProvider>
                  </MoneyWrapper>
                </RedoAdminClientContext.Provider>
              </RedoAdminMarketingRpcClientProvider>
            </RedoAdminAgentsRpcClientProvider>
          </RedoAdminRpcClientProvider>
        )}
      </AuthContext.Consumer>
    ),
  )(<RouterProvider router={router} />);
};

function MoneyWrapper({ children }: { children: React.ReactNode }) {
  const client = useContext(RedoAdminRpcClientContext);
  return (
    <CurrencyConverterProvider
      exchangeRates={async () =>
        new Map(
          Object.entries(await client!.getExchangeRates(null)) as [
            Currency,
            number,
          ][],
        )
      }
    >
      {children}
    </CurrencyConverterProvider>
  );
}

const root = createRoot(document.getElementById("root")!);
root.render(<AdminApp />);
